import { Box, Combobox, Group, Loader, Stack, Text, useCombobox } from "@mantine/core"
import { CaretDownIcon, UserIcon, XIcon } from "@phosphor-icons/react"
import { useSelector } from "react-redux"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSwr from "swr"
import { HttpRequest } from "../../../../utils"
import classes from "../analytics.module.css"
import { renderFullName } from "../../../../utils/render-name"

type StaffItem = { id: string; firstName?: string; lastName?: string; username?: string }
type StaffResponse = { rows?: StaffItem[]; items?: StaffItem[] } | StaffItem[]

type Props = {
  value: string | null
  onChange: (next: string | null) => void
}

function staffName(s: StaffItem): string {
  const name = s.lastName || s.firstName ? renderFullName(s) : ""
  return name || s.username || s.id
}

export function EmployeeFilter({ value, onChange }: Props) {
  const { t } = useTranslation(["dashboard"])
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() })
  const businessId = useSelector((state: { auth: { user?: { businessId?: string } } }) => state.auth?.user?.businessId) || ""
  const [query, setQuery] = useState("")

  const httpRequest = new HttpRequest()
  const { data: items = [], isLoading } = useSwr<StaffItem[]>(
    businessId ? `staff.select.${businessId}.${query}` : null,
    async () => {
      try {
        const res = await httpRequest.get<StaffResponse>("/ord/staff/select", { businessId, query: query || undefined, limit: 50 })
        const raw = Array.isArray(res) ? res : (res?.rows ?? res?.items ?? [])
        return raw
      } catch { return [] }
    },
    { revalidateOnFocus: false }
  )

  const selected = items.find(i => i.id === value)
  const selectedLabel = selected ? staffName(selected) : null

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val || null)
        combobox.closeDropdown()
      }}
      width={240}
      position="bottom-start"
    >
      <Combobox.Target>
        <Box
          component="button"
          type="button"
          className={classes.filterBtn}
          data-active={value ? true : undefined}
          onClick={() => combobox.toggleDropdown()}
        >
          <UserIcon size={16} />
          <Text component="span" inherit>
            {selectedLabel ?? t("analytics.filters.employee", { defaultValue: "Ажилтан" })}
          </Text>
          {value ? (
            <XIcon
              size={14}
              aria-label={t("analytics.filters.all", { defaultValue: "Бүгд" })}
              onClick={(e) => { e.stopPropagation(); onChange(null) }}
            />
          ) : (
            <CaretDownIcon size={14} />
          )}
        </Box>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Search
          placeholder={t("analytics.filters.search", { defaultValue: "Хайх..." })}
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
        <Combobox.Options>
          {isLoading ? (
            <Group justify="center" p="sm"><Loader size="xs" /></Group>
          ) : items.length === 0 ? (
            <Combobox.Empty>{t("analytics.emptyState", { defaultValue: "Өгөгдөл байхгүй" })}</Combobox.Empty>
          ) : (
            <>
              <Combobox.Option value="">
                <Text component="span" c="dimmed">{t("analytics.filters.all", { defaultValue: "Бүгд" })}</Text>
              </Combobox.Option>
              {items.map(s => (
                <Combobox.Option key={s.id} value={s.id}>
                  <Stack gap={0}>
                    <Text fw={value === s.id ? 600 : 400}>{staffName(s)}</Text>
                    {s.username ? <Text c="dimmed">{s.username}</Text> : null}
                  </Stack>
                </Combobox.Option>
              ))}
            </>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
